import React, { useState } from "react";
import {
  Heart,
  MessageCircle,
  Send,
  Bookmark,
  MoreHorizontal,
  ThumbsUp,
  Share2,
  Globe,
  Image as ImageIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Linkedin as LinkedinIcon,
  Music2,
  Repeat2,
  BarChart3,
  Volume2,
  Play,
  Lock,
  CheckCircle2,
} from "lucide-react";

function formatTime(iso) {
  if (!iso) return "Just now";
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch (_e) {
    return "Just now";
  }
}

function Avatar({ name }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] flex items-center justify-center text-[color:var(--bg-0)] text-sm font-semibold shrink-0">
      {initial}
    </div>
  );
}

function InstagramPreview({ post, pageName, handle }) {
  const cleanHandle = (handle || (pageName || "your_business").toLowerCase().replace(/\s+/g, "_"));
  const caption = post.caption || "";
  const hashtags = post.hashtags || [];
  return (
    <div
      className="rounded-[14px] bg-white text-neutral-900 shadow-[0_8px_30px_rgba(0,0,0,0.35)] overflow-hidden mx-auto w-full max-w-[420px]"
      data-testid="ig-preview"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-200">
        <Avatar name={pageName} />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold leading-tight truncate">{cleanHandle}</div>
          <div className="text-[11px] text-neutral-500 leading-tight">Sponsored · {formatTime(post.scheduled_at || post.published_at)}</div>
        </div>
        <MoreHorizontal className="h-4 w-4 text-neutral-500" />
      </div>
      {/* Image */}
      {post.image_url ? (
        <img src={post.image_url} alt="" className="w-full aspect-square object-cover" />
      ) : (
        <div className="w-full aspect-square bg-gradient-to-br from-neutral-100 to-neutral-200 flex items-center justify-center text-neutral-400">
          <ImageIcon className="h-8 w-8" />
        </div>
      )}
      {/* Actions */}
      <div className="px-3 pt-2 pb-1 flex items-center gap-3 text-neutral-900">
        <Heart className="h-6 w-6" />
        <MessageCircle className="h-6 w-6" />
        <Send className="h-6 w-6" />
        <Bookmark className="h-6 w-6 ml-auto" />
      </div>
      <div className="px-3 text-[13px] font-semibold">128 likes</div>
      <div className="px-3 pt-1 pb-3 text-[13px] leading-snug">
        <span className="font-semibold">{cleanHandle}</span>{" "}
        <span className="whitespace-pre-line">{caption}</span>
        {hashtags.length ? (
          <span className="text-[#1d4ed8]">
            {" "}
            {hashtags.map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ")}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function FacebookPreview({ post, pageName }) {
  const caption = post.caption || "";
  const hashtags = post.hashtags || [];
  const cta = post.cta || "";
  return (
    <div
      className="rounded-[8px] bg-white text-neutral-900 shadow-[0_8px_30px_rgba(0,0,0,0.35)] overflow-hidden mx-auto w-full max-w-[500px]"
      data-testid="fb-preview"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 pt-3">
        <Avatar name={pageName} />
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold leading-tight truncate">{pageName || "Your Page"}</div>
          <div className="text-[11px] text-neutral-500 leading-tight inline-flex items-center gap-1">
            {formatTime(post.scheduled_at || post.published_at)} · <Globe className="h-3 w-3" />
          </div>
        </div>
        <MoreHorizontal className="h-4 w-4 text-neutral-500" />
      </div>
      {/* Body */}
      <div className="px-3 pt-2 pb-3 text-[14px] leading-relaxed">
        <span className="whitespace-pre-line">{caption}</span>
        {cta ? <div className="mt-2 font-semibold">{cta}</div> : null}
        {hashtags.length ? (
          <div className="mt-2 text-[#1877F2]">
            {hashtags.map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ")}
          </div>
        ) : null}
      </div>
      {/* Image */}
      {post.image_url ? (
        <img src={post.image_url} alt="" className="w-full max-h-[460px] object-cover" />
      ) : null}
      {/* Engagement bar */}
      <div className="px-3 py-2 flex items-center gap-2 text-[12px] text-neutral-500 border-t border-neutral-200">
        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[#1877F2] text-white">
          <ThumbsUp className="h-3 w-3" />
        </span>
        <span>You and 142 others</span>
        <span className="ml-auto">12 comments · 3 shares</span>
      </div>
      <div className="px-2 py-1 grid grid-cols-3 gap-1 border-t border-neutral-200 text-[13px] text-neutral-700 font-medium">
        <button className="py-2 hover:bg-neutral-100 rounded inline-flex items-center justify-center gap-1.5">
          <ThumbsUp className="h-4 w-4" /> Like
        </button>
        <button className="py-2 hover:bg-neutral-100 rounded inline-flex items-center justify-center gap-1.5">
          <MessageCircle className="h-4 w-4" /> Comment
        </button>
        <button className="py-2 hover:bg-neutral-100 rounded inline-flex items-center justify-center gap-1.5">
          <Share2 className="h-4 w-4" /> Share
        </button>
      </div>
    </div>
  );
}

// ---------------- X (Twitter) ----------------
// 280-char cap is enforced for X — we truncate caption+hashtags client-side
// so the preview shows the *actual* publishable tweet, not a hopeful longer
// caption. The chosen strategy is "single caption, smart truncate for X"
// per product decision.
function buildXTweetText(post) {
  const captionRaw = (post.caption || "").trim();
  const tags = (post.hashtags || []).filter(Boolean).join(" ");
  const X_MAX = 280;
  if (!captionRaw && !tags) return { text: "", over: 0, charCount: 0 };
  const naive = tags ? `${captionRaw}\n\n${tags}` : captionRaw;
  if (naive.length <= X_MAX) {
    return { text: naive, over: 0, charCount: naive.length };
  }
  // Try keeping ALL hashtags + trimmed caption.
  const tagBlock = tags ? `\n\n${tags}` : "";
  const room = Math.max(0, X_MAX - tagBlock.length - 1);
  if (room >= 40) {
    const truncated = captionRaw.slice(0, room - 1).trimEnd() + "…";
    const text = truncated + tagBlock;
    return { text, over: Math.max(0, text.length - X_MAX), charCount: text.length };
  }
  // Caption is huge AND tags are huge — just truncate the whole thing.
  const fallback = naive.slice(0, X_MAX - 1).trimEnd() + "…";
  return { text: fallback, over: 0, charCount: fallback.length };
}

function XPreview({ post, pageName, handle }) {
  const cleanHandle = handle || (pageName || "your_business").toLowerCase().replace(/\s+/g, "_");
  const { text, charCount } = buildXTweetText(post);
  return (
    <div
      className="rounded-2xl bg-black text-white shadow-[0_8px_30px_rgba(0,0,0,0.45)] overflow-hidden mx-auto w-full max-w-[480px] border border-neutral-800"
      data-testid="x-preview"
    >
      <div className="flex gap-3 px-3 py-3">
        <Avatar name={pageName} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 text-[14px]">
            <span className="font-semibold text-white truncate">{pageName}</span>
            <CheckCircle2 className="h-4 w-4 text-sky-500 shrink-0" />
            <span className="text-neutral-500 text-[13px] truncate">@{cleanHandle} · 1m</span>
            <MoreHorizontal className="h-4 w-4 text-neutral-500 ml-auto shrink-0" />
          </div>
          <div className="text-[15px] leading-snug whitespace-pre-wrap text-neutral-100 mt-1">
            {text || "Your tweet preview will appear here."}
          </div>
          {post.image_url ? (
            <div className="mt-2 rounded-2xl overflow-hidden border border-neutral-800">
              <img src={post.image_url} alt="x-media" className="w-full aspect-[1.91/1] object-cover" />
            </div>
          ) : null}
          <div className="flex items-center justify-between mt-3 text-neutral-500 text-[12px]">
            <span className="inline-flex items-center gap-1"><MessageCircle className="h-4 w-4" /> 2</span>
            <span className="inline-flex items-center gap-1"><Repeat2 className="h-4 w-4" /> 4</span>
            <span className="inline-flex items-center gap-1"><Heart className="h-4 w-4" /> 18</span>
            <span className="inline-flex items-center gap-1"><BarChart3 className="h-4 w-4" /> 312</span>
            <span className="inline-flex items-center gap-1"><Send className="h-4 w-4" /></span>
          </div>
          <div className="text-[11px] text-neutral-500 mt-2">
            {charCount}/280 characters
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------- LinkedIn ----------------
function LinkedInPreview({ post, pageName }) {
  const caption = post.caption || "";
  const hashtags = post.hashtags || [];
  return (
    <div
      className="rounded-md bg-white text-neutral-900 shadow-[0_8px_30px_rgba(0,0,0,0.30)] overflow-hidden mx-auto w-full max-w-[460px] border border-neutral-200"
      data-testid="linkedin-preview"
    >
      <div className="flex items-start gap-3 px-3 py-3">
        <Avatar name={pageName} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[14px] text-neutral-900 truncate">{pageName}</div>
          <div className="text-[12px] text-neutral-500 truncate">Local business · Promoted</div>
          <div className="text-[11px] text-neutral-500 flex items-center gap-1 mt-0.5">
            1m · <Globe className="h-3 w-3" />
          </div>
        </div>
        <MoreHorizontal className="h-4 w-4 text-neutral-500" />
      </div>
      <div className="px-3 pb-2 text-[14px] whitespace-pre-wrap text-neutral-900 leading-snug">
        {caption || "Your LinkedIn post preview will appear here."}
      </div>
      {hashtags.length > 0 ? (
        <div className="px-3 pb-2 text-[13px] text-[#0a66c2] flex flex-wrap gap-x-1">
          {hashtags.slice(0, 8).map((h) => (
            <span key={h}>{h}</span>
          ))}
        </div>
      ) : null}
      {post.image_url ? (
        <img src={post.image_url} alt="linkedin-media" className="w-full aspect-[1.91/1] object-cover" />
      ) : null}
      <div className="px-3 py-2 text-[12px] text-neutral-500 flex items-center gap-2 border-t border-neutral-200">
        <span className="inline-flex items-center gap-1"><ThumbsUp className="h-3.5 w-3.5 text-[#0a66c2]" /></span>
        <span>42 reactions</span>
        <span className="ml-auto">6 comments</span>
      </div>
      <div className="grid grid-cols-4 border-t border-neutral-200 text-[13px] font-medium text-neutral-700">
        <button className="py-2 hover:bg-neutral-100 inline-flex items-center justify-center gap-1.5">
          <ThumbsUp className="h-4 w-4" /> Like
        </button>
        <button className="py-2 hover:bg-neutral-100 inline-flex items-center justify-center gap-1.5">
          <MessageCircle className="h-4 w-4" /> Comment
        </button>
        <button className="py-2 hover:bg-neutral-100 inline-flex items-center justify-center gap-1.5">
          <Repeat2 className="h-4 w-4" /> Repost
        </button>
        <button className="py-2 hover:bg-neutral-100 inline-flex items-center justify-center gap-1.5">
          <Send className="h-4 w-4" /> Send
        </button>
      </div>
    </div>
  );
}

// ---------------- TikTok ----------------
// TikTok is a vertical-video-first surface. We render a 9:16 frame with the
// post image (or gradient placeholder) and overlay the platform UI chrome
// so users can sanity-check the caption length and CTA before publishing.
function TikTokPreview({ post, pageName, handle }) {
  const cleanHandle = handle || (pageName || "your_business").toLowerCase().replace(/\s+/g, "_");
  const caption = post.caption || "";
  const hashtags = post.hashtags || [];
  return (
    <div
      className="relative rounded-2xl bg-black text-white shadow-[0_8px_30px_rgba(0,0,0,0.45)] overflow-hidden mx-auto w-full max-w-[300px] aspect-[9/16] border border-neutral-800"
      data-testid="tiktok-preview"
    >
      {post.image_url ? (
        <img src={post.image_url} alt="tiktok-media" className="absolute inset-0 w-full h-full object-cover opacity-90" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-pink-500/40 via-fuchsia-500/30 to-cyan-400/40" />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/70" />

      {/* Top chrome */}
      <div className="absolute top-3 left-0 right-0 flex justify-center gap-4 text-[11px] uppercase tracking-wider font-semibold text-white/90">
        <span className="opacity-60">Following</span>
        <span className="border-b-2 border-white pb-0.5">For You</span>
      </div>

      {/* Bottom caption + author */}
      <div className="absolute left-3 right-14 bottom-3 space-y-1.5">
        <div className="font-semibold text-[14px]">@{cleanHandle}</div>
        <div className="text-[12.5px] leading-tight whitespace-pre-wrap line-clamp-4">
          {caption || "Your TikTok caption preview will appear here."}
        </div>
        {hashtags.length > 0 ? (
          <div className="text-[12px] text-white/90 line-clamp-1">
            {hashtags.slice(0, 6).join(" ")}
          </div>
        ) : null}
        <div className="inline-flex items-center gap-1.5 mt-1 text-[11px] bg-black/40 px-2 py-1 rounded-full">
          <Volume2 className="h-3 w-3" />
          <span className="truncate max-w-[180px]">Original sound · {pageName}</span>
        </div>
      </div>

      {/* Right rail */}
      <div className="absolute right-2 bottom-4 flex flex-col items-center gap-3 text-white">
        <div className="h-9 w-9 rounded-full bg-white text-pink-600 flex items-center justify-center text-sm font-bold border-2 border-white">
          {(pageName || "?").charAt(0).toUpperCase()}
        </div>
        <button className="flex flex-col items-center text-[10px]">
          <Heart className="h-6 w-6" />
          12.4K
        </button>
        <button className="flex flex-col items-center text-[10px]">
          <MessageCircle className="h-6 w-6" />
          812
        </button>
        <button className="flex flex-col items-center text-[10px]">
          <Bookmark className="h-6 w-6" />
          340
        </button>
        <button className="flex flex-col items-center text-[10px]">
          <Share2 className="h-6 w-6" />
          Share
        </button>
      </div>

      {/* Play affordance */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-50">
        <Play className="h-10 w-10" />
      </div>
    </div>
  );
}

export default function PostPreviewCard({
  post,
  pageName,
  handle,
  platform: forcedPlatform,
  availability,
  testidPrefix = "post-preview",
}) {
  // Tabs ordered to match the rest of the app: IG, FB, X, LinkedIn.
  // TikTok preview component is kept below for future re-enable, but the
  // tab itself is hidden from MVP since Postmoro does not ship TikTok yet.
  const TABS = [
    { id: "instagram", label: "Instagram", Icon: InstagramIcon },
    { id: "facebook", label: "Facebook", Icon: FacebookIcon },
    { id: "x", label: "X / Twitter", Icon: TwitterIcon },
    { id: "linkedin", label: "LinkedIn", Icon: LinkedinIcon },
  ];

  // Resolve initial tab — caller can force a platform (e.g. the Generate
  // page when only one platform is selected) so the preview matches the
  // selection without an extra click.
  const platformKey = (() => {
    if (!forcedPlatform) return "instagram";
    const p = String(forcedPlatform).toLowerCase();
    if (p.includes("instagram")) return "instagram";
    if (p.includes("facebook")) return "facebook";
    if (p.includes("x") || p.includes("twitter")) return "x";
    if (p.includes("linkedin")) return "linkedin";
    // TikTok intentionally falls through to default (hidden from MVP).
    return "instagram";
  })();

  const [active, setActive] = useState(platformKey);
  if (!post) return null;

  const resolvedPageName = pageName || post.published_page_name || "Your Business";

  // ``availability`` is an optional map: { instagram: "ok"|"locked"|"coming_soon", ... }
  const platformAvailability = (availability || {});
  const lockedReason = (id) => {
    const v = platformAvailability[id];
    if (v === "locked") return "Requires Meta approval";
    if (v === "coming_soon") return id === "linkedin" ? "Planned" : "Coming soon";
    if (v === "not_connected") return "Not connected";
    return null;
  };

  const renderPreview = () => {
    switch (active) {
      case "facebook":
        return <FacebookPreview post={post} pageName={resolvedPageName} />;
      case "x":
        return <XPreview post={post} pageName={resolvedPageName} handle={handle} />;
      case "linkedin":
        return <LinkedInPreview post={post} pageName={resolvedPageName} />;
      // TikTokPreview retained below for future re-enable.
      case "instagram":
      default:
        return <InstagramPreview post={post} pageName={resolvedPageName} handle={handle} />;
    }
  };

  const activeLabel = TABS.find((t) => t.id === active)?.label || "Instagram";
  const activeLocked = lockedReason(active);

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-wrapper`}>
      {/* Tabs */}
      <div
        className="flex flex-wrap rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-1 gap-1"
        data-testid={`${testidPrefix}-tabs`}
      >
        {TABS.map((t) => {
          const Icon = t.Icon;
          const locked = lockedReason(t.id);
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActive(t.id)}
              data-testid={`${testidPrefix}-tab-${t.id}`}
              title={locked || undefined}
              className={
                "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded font-medium transition " +
                (active === t.id
                  ? "bg-[color:var(--surface-1)] text-[color:var(--text-1)] shadow-[inset_0_0_0_1px_rgba(27,168,159,0.35)]"
                  : "text-[color:var(--text-2)] hover:text-[color:var(--text-1)]")
              }
            >
              <Icon className="h-3.5 w-3.5" />
              <span>{t.label}</span>
              {locked ? <Lock className="h-3 w-3 text-[color:var(--text-3)]" /> : null}
            </button>
          );
        })}
      </div>

      {renderPreview()}

      <div className="text-[11px] text-[color:var(--text-3)] text-center">
        {activeLocked ? (
          <span className="text-[color:var(--accent-warm)]">
            Publishing to {activeLabel} is unavailable: {activeLocked}. Preview only.
          </span>
        ) : (
          <>Live preview · how it will appear on {activeLabel}</>
        )}
      </div>
    </div>
  );
}
